import type { BoardSegmentTemplate } from './schema';

/**
 * The board is generated from authored segments rather than hand-placed nodes.
 * Each segment owns a stretch of columns for one life stage and declares how
 * many parallel routes exist per column. A seeded generator then draws node
 * templates from the segment's pool.
 *
 * This keeps the board feeling designed (the anchors and stage rhythm are
 * authored) while guaranteeing that no two lives walk exactly the same path.
 */
export const BOARD_SEGMENTS: readonly BoardSegmentTemplate[] = [
  {
    id: 'seg.launch',
    stage: 'launch',
    label: 'Launch',
    columns: 4,
    lanesPerColumn: [2, 3, 2, 1],
    pool: [
      { kind: 'education', title: 'Study', subtitle: 'Commit years to a credential', pools: ['pool.education'], actions: ['education'], weight: 3 },
      { kind: 'career', title: 'Work', subtitle: 'Earn now, learn on the job', pools: ['pool.career.entry'], actions: ['career'], weight: 3 },
      { kind: 'event', title: 'Eighteen', subtitle: 'The first year nobody plans for you', pools: ['pool.young'], weight: 2 },
      { kind: 'relationship', title: 'People', subtitle: 'Who you spend your twenties with', pools: ['pool.social', 'pool.romance.early'], actions: ['relationships'], weight: 2 },
      { kind: 'leisure', title: 'Breathe', subtitle: 'Rest is not a wasted turn', pools: ['pool.leisure'], actions: ['leisure'], weight: 1 },
      { kind: 'finance', title: 'Money', subtitle: 'First real decisions about it', pools: ['pool.finance.early'], actions: ['finance'], weight: 2 },
    ],
    anchor: { kind: 'milestone', title: 'Adulthood', subtitle: 'No one is coming to organise this', milestone: true, pools: ['pool.milestone.launch'] },
  },
  {
    id: 'seg.earlyCareer',
    stage: 'earlyCareer',
    label: 'Early career',
    columns: 5,
    lanesPerColumn: [2, 3, 2, 3, 1],
    pool: [
      { kind: 'career', title: 'Career', subtitle: 'Push, or coast', pools: ['pool.career.early'], actions: ['career'], weight: 4 },
      { kind: 'opportunity', title: 'Opening', subtitle: 'Something unexpected', pools: ['pool.opportunity'], weight: 2 },
      { kind: 'relationship', title: 'Dating', subtitle: 'Finding out what you want', pools: ['pool.romance.early'], actions: ['relationships'], weight: 3 },
      { kind: 'finance', title: 'Money', subtitle: 'Debt, savings, lifestyle', pools: ['pool.finance.early'], actions: ['finance'], weight: 3 },
      { kind: 'education', title: 'Upskill', subtitle: 'Go back, part-time or full', pools: ['pool.education'], actions: ['education'], weight: 2 },
      { kind: 'health', title: 'Body', subtitle: 'Habits that compound', pools: ['pool.health.early'], actions: ['health'], weight: 2 },
      { kind: 'leisure', title: 'Life', subtitle: 'Friends, travel, nothing at all', pools: ['pool.leisure'], actions: ['leisure'], weight: 2 },
      { kind: 'event', title: 'Twenties', subtitle: '', pools: ['pool.young', 'pool.social'], weight: 2 },
      { kind: 'investment', title: 'Invest', subtitle: 'Or wait until it feels safe', pools: ['pool.investment'], actions: ['invest'], weight: 2 },
      { kind: 'event', title: 'Later', subtitle: 'What you keep meaning to do', pools: ['pool.inertia', 'pool.stress'], weight: 2 },
    ],
    anchor: { kind: 'crossroads', title: 'Twenty-eight', subtitle: 'The first real fork', milestone: true, pools: ['pool.milestone.28'] },
  },
  {
    id: 'seg.establishing',
    stage: 'establishing',
    label: 'Establishing',
    columns: 5,
    lanesPerColumn: [3, 2, 3, 2, 1],
    pool: [
      { kind: 'career', title: 'Career', subtitle: 'Seniority costs something', pools: ['pool.career.mid'], actions: ['career'], weight: 4 },
      { kind: 'family', title: 'Family', subtitle: 'Partnership, children, neither', pools: ['pool.family', 'pool.romance.commit'], actions: ['family'], weight: 4 },
      { kind: 'finance', title: 'Money', subtitle: 'Property, saving, spending', pools: ['pool.finance.mid'], actions: ['finance'], weight: 3 },
      { kind: 'business', title: 'Venture', subtitle: 'Your own thing, maybe', pools: ['pool.business'], actions: ['business'], weight: 2 },
      { kind: 'investment', title: 'Invest', subtitle: 'Compounding starts to show', pools: ['pool.investment'], actions: ['invest'], weight: 3 },
      { kind: 'health', title: 'Health', subtitle: 'The first warnings', pools: ['pool.health.mid'], actions: ['health'], weight: 2 },
      { kind: 'risk', title: 'Pressure', subtitle: 'Something has to give', pools: ['pool.stress'], weight: 2 },
      { kind: 'leisure', title: 'Rest', subtitle: 'Before it becomes mandatory', pools: ['pool.leisure'], actions: ['leisure'], weight: 2 },
      { kind: 'event', title: 'Later', subtitle: 'What you keep meaning to do', pools: ['pool.inertia', 'pool.regret'], weight: 2 },
    ],
    anchor: { kind: 'milestone', title: 'Thirty-five', subtitle: 'The shape of it is visible now', milestone: true, pools: ['pool.milestone.35'] },
  },
  {
    id: 'seg.growth',
    stage: 'growth',
    label: 'Growth',
    columns: 6,
    lanesPerColumn: [2, 3, 3, 2, 3, 1],
    pool: [
      { kind: 'career', title: 'Career', subtitle: 'Peak earning years', pools: ['pool.career.mid', 'pool.career.senior'], actions: ['career'], weight: 4 },
      { kind: 'family', title: 'Family', subtitle: 'Children, ageing parents, both', pools: ['pool.family', 'pool.family.parents'], actions: ['family'], weight: 4 },
      { kind: 'finance', title: 'Money', subtitle: 'The decade that decides retirement', pools: ['pool.finance.mid'], actions: ['finance'], weight: 3 },
      { kind: 'business', title: 'Venture', subtitle: '', pools: ['pool.business'], actions: ['business'], weight: 2 },
      { kind: 'investment', title: 'Invest', subtitle: '', pools: ['pool.investment'], actions: ['invest'], weight: 3 },
      { kind: 'risk', title: 'Burnout', subtitle: 'The bill for the last ten years', pools: ['pool.stress', 'pool.burnout'], weight: 3 },
      { kind: 'health', title: 'Health', subtitle: '', pools: ['pool.health.mid'], actions: ['health'], weight: 3 },
      { kind: 'relationship', title: 'People', subtitle: 'Friendships need maintenance', pools: ['pool.social', 'pool.relationship.strain'], actions: ['relationships'], weight: 3 },
      { kind: 'risk', title: 'Shock', subtitle: 'Something you did not plan for', pools: ['pool.crisis'], weight: 2 },
      { kind: 'leisure', title: 'Rest', subtitle: '', pools: ['pool.leisure'], actions: ['leisure'], weight: 2 },
    ],
    anchor: { kind: 'crossroads', title: 'Forty-five', subtitle: 'Is this the life you meant to build?', milestone: true, pools: ['pool.milestone.45'] },
  },
  {
    id: 'seg.midlife',
    stage: 'midlife',
    label: 'Midlife',
    columns: 5,
    lanesPerColumn: [3, 2, 3, 2, 1],
    pool: [
      { kind: 'career', title: 'Career', subtitle: 'Plateau, pivot, or press on', pools: ['pool.career.senior', 'pool.career.pivot'], actions: ['career'], weight: 4 },
      { kind: 'recovery', title: 'Rebuild', subtitle: 'After whatever broke', pools: ['pool.recovery'], weight: 3 },
      { kind: 'family', title: 'Family', subtitle: 'Teenagers, parents, partners', pools: ['pool.family.teen', 'pool.family.parents'], actions: ['family'], weight: 4 },
      { kind: 'health', title: 'Health', subtitle: 'It stops being theoretical', pools: ['pool.health.late'], actions: ['health'], weight: 3 },
      { kind: 'finance', title: 'Money', subtitle: 'Retirement maths gets real', pools: ['pool.finance.late'], actions: ['finance'], weight: 3 },
      { kind: 'investment', title: 'Invest', subtitle: '', pools: ['pool.investment'], actions: ['invest'], weight: 2 },
      { kind: 'business', title: 'Venture', subtitle: '', pools: ['pool.business'], actions: ['business'], weight: 2 },
      { kind: 'event', title: 'Reckoning', subtitle: 'What you postponed', pools: ['pool.inertia', 'pool.regret'], weight: 3 },
    ],
    anchor: { kind: 'milestone', title: 'Fifty-five', subtitle: 'The last long runway', milestone: true, pools: ['pool.milestone.55'] },
  },
  {
    id: 'seg.lateCareer',
    stage: 'lateCareer',
    label: 'Late career',
    columns: 4,
    lanesPerColumn: [2, 3, 2, 1],
    pool: [
      { kind: 'career', title: 'Career', subtitle: 'Mentor, coast, or cash out', pools: ['pool.career.late'], actions: ['career'], weight: 4 },
      { kind: 'finance', title: 'Money', subtitle: 'Sequencing matters now', pools: ['pool.finance.late'], actions: ['finance'], weight: 4 },
      { kind: 'family', title: 'Family', subtitle: 'Adult children, grandchildren', pools: ['pool.family.adult'], actions: ['family'], weight: 3 },
      { kind: 'health', title: 'Health', subtitle: '', pools: ['pool.health.late'], actions: ['health'], weight: 3 },
      { kind: 'legacy', title: 'Legacy', subtitle: 'Pass something on', pools: ['pool.legacy'], weight: 2 },
      { kind: 'leisure', title: 'Time', subtitle: 'Finally', pools: ['pool.leisure'], actions: ['leisure'], weight: 2 },
      { kind: 'investment', title: 'Invest', subtitle: '', pools: ['pool.investment'], actions: ['invest'], weight: 2 },
    ],
    anchor: { kind: 'milestone', title: 'Sixty-four', subtitle: 'The decision you have been avoiding', milestone: true, pools: ['pool.milestone.64'] },
  },
  {
    id: 'seg.preRetirement',
    stage: 'preRetirement',
    label: 'Transition',
    columns: 3,
    lanesPerColumn: [2, 2, 1],
    pool: [
      { kind: 'retirement', title: 'Retire', subtitle: 'Stop, or not yet', pools: ['pool.retirement'], actions: ['retire'], weight: 4 },
      { kind: 'finance', title: 'Money', subtitle: 'Drawdown planning', pools: ['pool.finance.retire'], actions: ['finance'], weight: 3 },
      { kind: 'health', title: 'Health', subtitle: '', pools: ['pool.health.late'], actions: ['health'], weight: 2 },
      { kind: 'family', title: 'Family', subtitle: '', pools: ['pool.family.adult'], actions: ['family'], weight: 2 },
      { kind: 'legacy', title: 'Legacy', subtitle: '', pools: ['pool.legacy'], weight: 2 },
    ],
    anchor: { kind: 'retirement', title: 'Retirement', subtitle: 'The last employer is behind you', milestone: true, pools: ['pool.retirement'], actions: ['retire'] },
  },
  {
    id: 'seg.retirement',
    stage: 'retirement',
    label: 'Retirement',
    columns: 4,
    lanesPerColumn: [2, 2, 2, 1],
    pool: [
      { kind: 'leisure', title: 'Time', subtitle: 'All of it, yours', pools: ['pool.retired.leisure'], actions: ['leisure'], weight: 4 },
      { kind: 'family', title: 'Family', subtitle: 'Grandchildren, reconciliation', pools: ['pool.family.adult', 'pool.retired.family'], actions: ['family'], weight: 4 },
      { kind: 'health', title: 'Health', subtitle: '', pools: ['pool.health.old'], actions: ['health'], weight: 4 },
      { kind: 'finance', title: 'Money', subtitle: 'Does it last?', pools: ['pool.finance.retire'], actions: ['finance'], weight: 3 },
      { kind: 'legacy', title: 'Legacy', subtitle: '', pools: ['pool.legacy'], weight: 3 },
    ],
    anchor: { kind: 'milestone', title: 'Eighty', subtitle: 'Still here', milestone: true, pools: ['pool.retired.leisure'] },
  },
  {
    id: 'seg.lateLife',
    stage: 'lateLife',
    label: 'Late life',
    columns: 3,
    lanesPerColumn: [2, 1, 1],
    pool: [
      { kind: 'legacy', title: 'Legacy', subtitle: 'What stays behind', pools: ['pool.legacy'], weight: 4 },
      { kind: 'family', title: 'Family', subtitle: '', pools: ['pool.retired.family'], actions: ['family'], weight: 3 },
      { kind: 'health', title: 'Health', subtitle: '', pools: ['pool.health.old'], actions: ['health'], weight: 4 },
      { kind: 'leisure', title: 'Days', subtitle: '', pools: ['pool.retired.leisure'], actions: ['leisure'], weight: 3 },
    ],
    anchor: { kind: 'end', title: 'The end', subtitle: 'Your life, written down', milestone: true, pools: [] },
  },
];
